import { lazy } from "react";

// project imports
import MainLayout from "layout/MainLayout";
import Loadable from "layout/Loadable";
import Cache from "data/cache";

import disclaimersMd from "pages/legal/disclaimers.md";
import privacyMd from "pages/legal/privacy.md";
import termsMd from "pages/legal/terms.md";

// Page routing
const HomePage = Loadable(lazy(() => import("pages/home")));
const Experiences = Loadable(lazy(() => import("pages/experiences")));
const Blog = Loadable(lazy(() => import("pages/blog")));
const Testimonials = Loadable(lazy(() => import("pages/testimonials")));
const RedirectPage = Loadable(lazy(() => import("pages/redirect")));
const NotFoundPage = Loadable(lazy(() => import("pages/404")));
const BankOfKigali = Loadable(
  lazy(() => import("pages/storyboards/bank_of_kigali"))
);
const Texas4000 = Loadable(
  lazy(() => import("pages/storyboards/Texas4000"))
);
const TexasVoterTracker = Loadable(
  lazy(() => import("pages/storyboards/texas_voter_tracker"))
);
const MadeToUplift = Loadable(
  lazy(() => import("pages/storyboards/made_to_uplift"))
);
const WIPPage = Loadable(lazy(() => import("pages/wip")));
const MarkdownPage = Loadable(lazy(() => import("pages/markdown")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  element: <MainLayout />,
  children: [
    {
      path: "/",
      element: <HomePage />,
    },

    // CAREER
    {
      path: "professional",
      element: (
        <Experiences
          category="Work"
          title="Professional Experience"
          subtitle="I began my career in Big Tech and have transitioned to launching my own social enterprises."
          fetcher={Cache.fetchExperiences}
        />
      ),
    },
    {
      path: "impact",
      element: (
        <Experiences
          category="Social Impact"
          title="Social Impact"
          subtitle="A sampling of my community engagement work, from volunteering to larger endeavors"
          fetcher={Cache.fetchExperiences}
        />
      ),
    },
    {
      path: "awards",
      element: (
        <Experiences
          category="Awards"
          title="Awards"
          subtitle="(non-exhaustive)"
          fetcher={Cache.fetchAwards}
        />
      ),
    },
    {
      path: "testimonials",
      element: <Testimonials />,
    },
    {
      path: "resumes",
      element: <WIPPage />,
    },
    {
      path: "resume",
      element: (
        <RedirectPage to="https://drive.google.com/file/d/1BqZu4Nm907UTR7TS9aYtyVkNNw2vKEpj/view?usp=sharing" />
      ),
    },
    {
      path: "calendar",
      element: (
        <RedirectPage to="https://calendar.app.google/sJjKWESJnnKaxECBA" />
      ),
    },
    {
      path: "a4li",
      element: (
        <RedirectPage to="https://drive.google.com/file/d/13EcJZurz4U7Q0NK_V-OD6etm3OwjF50o/view?usp=sharing" />
      ),
    },
    {
      path: "timeline",
      element: <WIPPage />,
    },

    // PORTFOLIO
    {
      path: "portfolio",
      element: (
        <Experiences
          category="Portfolio"
          title="Full Portfolio"
          subtitle="Passion projects make me really happy :)"
          disclaimers={[
            "Below, you'll find a *sampling* of my work. My early projects started with simple frameworks that gradually evolved my skills to tackle more complex problems.",
            "My intention is show you that I love to tinker and build -- my passion projects often make me so excited that I pull caffeine-free all-nighters to work on them.",
          ]}
          fetcher={Cache.fetchExperiences}
        />
      ),
    },
    {
      path: "bank-of-kigali",
      element: <BankOfKigali />,
    },
    {
      path: "2022-tracker",
      element: <TexasVoterTracker year={2022} subtitle="The Latest Release" />,
    },
    {
      path: "2020-tracker",
      element: (
        <TexasVoterTracker year={2020} subtitle="The Original Release" />
      ),
    },
    {
      path: "made-to-uplift",
      element: <MadeToUplift />,
    },
    {
      path: "evolution",
      element: (
        <Experiences
          category="Portfolio Evolution"
          title="Design Evolution"
          disclaimers={[
            "You can trace my growth not just as a builder, but as a designer through the evolution of this portfolio itself. Each version reflects a new chapter in how I think, build, and create.",
            "Note: Versions 3-5 share the same underlying database, but v5 introduces structural changes that may result in missing data on older versions.",
          ]}
          fetcher={Cache.fetchExperiences}
          ascending={true}
        />
      ),
    },
    {
      path: "uplift-journal",
      element: <WIPPage />,
    },
    {
      path: "memories",
      element: <WIPPage />,
    },

    // BLOG
    {
      path: "blog/hbs",
      element: (
        <Blog
          category="mba"
          title="Harvard Business School"
          subtitle="A series of entries from my MBA experience at Harvard"
        />
      ),
    },
    {
      path: "blog/tech",
      element: (
        <Blog
          category="tech"
          title="Tech"
          subtitle="Entries ranging from my work experience to my various passion projects and how they helped me break into tech"
        />
      ),
    },
    {
      path: "blog/musings",
      element: (
        <Blog
          category="life"
          title="Musings"
          subtitle="Miscellaneous thoughts on life"
        />
      ),
    },

    // LEGAL
    {
      path: "disclaimers",
      element: <MarkdownPage file={disclaimersMd} />,
    },
    {
      path: "privacy",
      element: <MarkdownPage file={privacyMd} />,
    },
    {
      path: "terms",
      element: <MarkdownPage file={termsMd} />,
    },

    // HOBBIES
    {
      path: "biking-to-alaska",
      element: <Texas4000 />,
    },
    {
      path: "alaska",
      element: <WIPPage />,
    },
    {
      path: "travel",
      element: <WIPPage />,
    },
    {
      path: "dance",
      element: <WIPPage />,
    },
    {
      path: "guitar",
      element: <WIPPage />,
    },
    {
      path: "quotes",
      element: <WIPPage />,
    },
    {
      path: "*",
      element: <NotFoundPage />,
    },
  ],
};

export default MainRoutes;
